import {
	CheckCircleFilled,
	CloseCircleFilled,
	ExclamationCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { employeeApi } from 'Api/employee';
import { napsaIntegrationServiceApi } from 'Api/napsaIntegration';
import {
	Button,
	Col,
	Collapse,
	CollapseProps,
	DatePicker,
	Input,
	Row,
	Tooltip,
} from 'antd';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import DatePickerField from 'components/Global/DatePicker';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import dayjs from 'dayjs';
import {
	EmployeeDetailsInterface,
	IdentificationDetailsInterface,
	PersonalDetailsInterface,
} from 'interfaces/employee.interface';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
	calculateAge,
	capitalizeFirstLetter,
	formatToDateOnly,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';
import { booleanOptions } from 'constants/Data';

type Props = {
	changeTab: (key: string) => void;
	onCancel: () => void;
	employeeId: string;
	employeeData: any;
	getEmployeeDetail: any;
	setEmployeeId: any;
	isPayrollRun: boolean;
	skipLeaveGratuityLocked?: boolean;
	nhimaAgeLimit: number;
	napsaAgeLimit: number;
	lastDateOfPayPeriod: any;
};
type StatusDetails = {
	isStatusChange: boolean;
	previousStatus: string;
	newStatus: string;
};
interface DropdownOption {
	label: string;
	value: string;
}
const stage = process.env.REACT_APP_STAGE || '';

const getRejoinTooltip = (employee: {
	isEligibleToRejoin?: boolean;
	rejoinMinDate?: string;
	rejoinValidationMessage?: string | null;
}) => {
	if (!employee?.isEligibleToRejoin) {
		return (
			employee?.rejoinValidationMessage ||
			'Rejoin is unavailable until leave and gratuity are settled or termination payroll is paid.'
		);
	}
	if (employee.rejoinMinDate) {
		const fromDate = dayjs(employee.rejoinMinDate).format('DD/MM/YYYY');
		return `Settlement completed and eligible for rejoin. Set status to Active with a joining date on or after ${fromDate}.`;
	}
	return 'Settlement completed. Set status to Active to start a new tenure.';
};

const EmploymentDetails = (props: Props) => {
	const permissionsContext = useContext(PermissionContext);

	const [searchParams, setSearchParams] = useSearchParams();
	const [isLoading, setIsLoading] = useState(false);
	const [isSSNRequired, setIsSSNRequired] = useState(false);

	const [isSSNVerified, setIsSSNVerified] = useState(false);
	const [isNRCVerified, setIsNRCVerified] = useState(false);
	const [isVerifyingSSN, setIsVerifyingSSN] = useState(false);
	const [isVerifyingNRC, setIsVerifyingNRC] = useState(false);

	const [isNhimaRequired, setIsNhimaRequired] = useState(false);
	const [isSSNVisible, setIsSSNVisible] = useState(false);
	const [showRelievingInfo, setShowRelievingInfo] = useState(false);
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isLoading,
			onclick: () => {
				onCancel();
			},
			size: 'small',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Save & Next',
			isLoading: isLoading,
			className: 'btn-blue',
			isSubmit: false,
			// disabled: !isNRCVerified || (isSSNRequired && !isSSNVerified),
			disabled: false,
			onclick: () => {
				handleSubmit();
			},
			size: 'small',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	const {
		changeTab,
		onCancel,
		employeeId,
		employeeData,
		getEmployeeDetail,
		setEmployeeId,
		isPayrollRun,
		skipLeaveGratuityLocked = false,
		nhimaAgeLimit,
		napsaAgeLimit,
		lastDateOfPayPeriod,
	} = props;

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data,
	);

	const [statusDetails, setStatusDetails] = useState<StatusDetails>({
		isStatusChange: false,
		previousStatus: '',
		newStatus: '',
	});

	const [storedStatus, setStoredStatus] = useState<string | null>(null);

	const [personalDetails, setPersonalDetails] =
		useState<PersonalDetailsInterface>({
			firstName: '',
			midName: '',
			lastName: '',
			dateOfBirth: '',
			gender: null,
			country: '',
			maritalStatus: null,
			referenceNumber: '',
			email: '',
		});

	const [identificationDetails, setIdentificationDetails] =
		useState<IdentificationDetailsInterface>({
			ssn: null,
			recordStatus: '',
			passportNo: '',
			nationalRegistrationNo: '',
			statusUpdatedAt: null,
			relievingDate: null,
			contractInDate: null,
			contractOutDate: null,
		});

	const [employeeDetails, setEmployeeDetails] =
		useState<EmployeeDetailsInterface>({
			joiningDate: '',
			branchId: '',
			departmentId: null,
			costCenterId: null,
			categoryId: '',
			jobGradeId: null,
			staffCode: '',
			nhimaNo: null,
			skipLeaveGratuity: false,
			// supervisorIds: [],
		});

	const [formError, setFormError] = useState<any>({
		firstName: false,
		lastName: false,
		midName: false,
		dateOfBirth: false,
		// gender: false,
		country: false,
		referenceNumber: false,
		// maritalStatus: false,
		// ssn: false,
		bankAccountNo: false,
		nationalRegistrationNo: false,
		joiningDate: false,
		branchId: false,
		// departmentId: false,
		// costCenterId: false,
		categoryId: false,
		// jobGradeId: false,
		staffCode: false,
		salaryRate: false,
		payMethod: false,
		totalSalary: false,
		napsaYearToDate: false,
		leavesDue: false,
		leavesDayMonth: false,
		recordStatus: false,
		email: false,
		// nhimaNumber: false, // supervisorIds: false,
	});

	const [isDisabled, setIsDisabled] = useState(false);

	useEffect(() => {
		if (
			employeeId &&
			!permissionsContext.allowedPermissions.includes('Edit_Employment')
		) {
			setIsDisabled(true);
		} else {
			setIsDisabled(false);
		}
	}, [employeeId, permissionsContext.allowedPermissions]);

	const handleChange = (
		value: string | number | null | string[],
		name: string,
		stateName: string,
		required: boolean,
		regex?: RegExp | null,
	) => {
		if (
			(name === 'referenceNumber' || name === 'midName') &&
			typeof value === 'string' &&
			invalidText(value)
		) {
			setFormError((prev: any) => {
				return {
					...prev,
					[name]: false,
				};
			});
		}
		if (name === 'dateOfBirth') {
			const age = calculateAge(value as string);

			age > napsaAgeLimit ? setIsSSNRequired(false) : setIsSSNRequired(true);
			age > nhimaAgeLimit
				? setIsNhimaRequired(false)
				: setIsNhimaRequired(true);
		}
		if (name === 'recordStatus') {
			if (value === storedStatus) {
				setStatusDetails({
					isStatusChange: false,
					previousStatus: identificationDetails.recordStatus,
					newStatus: value as string,
				});
			} else {
				setStatusDetails({
					isStatusChange: true,
					previousStatus: identificationDetails.recordStatus,
					newStatus: value as string,
				});
			}

			if (value === 'ACTIVE') {
				setIdentificationDetails((prev) => ({
					...prev,
					relievingDate: null,
				}));
				setShowRelievingInfo(true);
			} else {
				setShowRelievingInfo(false);
			}
		}

		if (name === 'branchId') {
			const updatedEmployeeDetails: any = { ...employeeDetails };
			updatedEmployeeDetails.departmentId = null;
			updatedEmployeeDetails.costCenterId = null;
			setEmployeeDetails(updatedEmployeeDetails);
		}
		if (name === 'departmentId') {
			const updatedEmployeeDetails: any = { ...employeeDetails };
			updatedEmployeeDetails.costCenterId = null;
			setEmployeeDetails(updatedEmployeeDetails);
		}

		if (name === 'ssn') {
			// Reset verification status when SSN changes
			setIsSSNVerified(false);
		}

		if (name === 'recordStatus') {
			if (value === storedStatus)
				setStatusDetails({
					isStatusChange: false,
					previousStatus: identificationDetails.recordStatus,
					newStatus: value as string,
				});
			else
				setStatusDetails({
					isStatusChange: true,
					previousStatus: identificationDetails.recordStatus,
					newStatus: value as string,
				});
		}

		if (name === 'nationalRegistrationNo') {
			// Reset verification status when NRC changes
			setIsNRCVerified(false);
		}

		OnChange(value, name, stateName);
	};

	const OnChange = (
		value: string | number | null | string[],
		key: string,
		stateName: string,
	) => {
		switch (stateName) {
			case 'personalDetails':
				setPersonalDetails((prev) => {
					return {
						...prev,
						[key]: value,
					};
				});
				break;
			case 'identificationDetails':
				setIdentificationDetails((prev) => {
					return {
						...prev,
						[key]: value,
					};
				});
				break;
			case 'employeeDetails':
				setEmployeeDetails((prev) => {
					return {
						...prev,
						[key]: value,
					};
				});
				break;
		}

		if (typeof value === 'string' && !invalidText(value)) {
			const checkFormError = validateFormData(
				{ [key]: value },
				{ ...formError },
			);
			setFormError(checkFormError);
		}
	};

	const handleVerifySSN = async () => {
		// Simulate SSN verification process
		if (stage === 'live') {
			try {
				setIsVerifyingSSN(true);
				// Here you would make an API call to verify the SSN
				const { data } = await napsaIntegrationServiceApi.verifyEmployeeSSN({
					kycNo: identificationDetails.ssn,
				});
				setIsSSNVerified(true);
				const apiResponseData = data?.data;

				setPersonalDetails((prev) => ({
					...prev,
					firstName: apiResponseData.firstName || prev.firstName,
					lastName: apiResponseData.lastName || prev.lastName,
				}));
				toastText('SSN verified successfully', 'success');
			} catch (error: any) {
				const errorMessage =
					error?.response?.data?.message || 'Failed to verify SSN';
				toastText(errorMessage, 'error');
				setIsSSNVerified(false);
			} finally {
				setIsVerifyingSSN(false);
			}
		} else {
			setIsSSNVerified(true);
			toastText(
				'This feature is temporarily disabled due to ongoing NAPSA integration.',
				'success',
			);
		}
	};

	const handleVerifyNRC = async () => {
		// Simulate NRC verification process
		if (stage === 'live') {
			try {
				setIsVerifyingNRC(true);
				// Here you would make an API call to verify the NRC
				const { data } = await napsaIntegrationServiceApi.verifyEmployeeNRC({
					nrcNo: identificationDetails.nationalRegistrationNo,
				});

				const apiResponseData = data?.data;
				setPersonalDetails((prev) => ({
					...prev,
					firstName: apiResponseData.firstName || prev.firstName,
					midName: apiResponseData.midName || prev.midName,
					lastName: apiResponseData.lastName || prev.lastName,
				}));
				setIsNRCVerified(true);
			} catch (error: any) {
				const errorMessage =
					error?.response?.data?.message || 'Failed to verify NRC';
				toastText(errorMessage, 'error');
				setIsNRCVerified(false);
			} finally {
				setIsVerifyingNRC(false);
			}
		} else {
			setIsNRCVerified(true);
			toastText(
				'This feature is temporarily disabled due to ongoing NAPSA integration.',
				'success',
			);
		}
	};

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);

			const response = await getAccessibleBranchForUser();

			const branches: DropdownOption[] = response?.data?.data ?? [];

			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message || 'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		fetchAccessibleBranches();
	}, []);

	const handleSubmit = async () => {
		const { isStatusChange, newStatus } = statusDetails;

		if (
			isStatusChange &&
			newStatus.toLowerCase() !== 'active' &&
			invalidText(identificationDetails.relievingDate)
		) {
			toastText(
				`Please provide a relieving date when setting status to "${capitalizeFirstLetter(
					newStatus,
				)}".`,
				'error',
			);

			return;
		}
		let checkFormError = validateFormData(
			{
				...personalDetails,
				...identificationDetails,
				...employeeDetails,
				// ...paymentDetails,
				// ...taxationDetails,
			},
			{ ...formError },
		);

		let SsnError = false;

		if (isSSNRequired) {
			SsnError = !/^\d{9}$/.test(identificationDetails.ssn as string);
		} else if (identificationDetails.ssn) {
			SsnError = !/^\d{9}$/.test(identificationDetails.ssn);
		}
		if (isNhimaRequired && invalidText(employeeDetails.nhimaNo)) {
			checkFormError = {
				...checkFormError,
				nhimaNo: true,
			};
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		checkFormError = {
			...checkFormError,
			ssn: SsnError,

			referenceNumber: personalDetails.referenceNumber
				? !/^(?!-+$)[a-zA-Z0-9-]+$/.test(personalDetails.referenceNumber)
				: false,
			midName: personalDetails.midName
				? !/^[a-zA-Z ]+$/.test(personalDetails.midName)
				: false,
			email: personalDetails.email
				? !emailRegex.test(personalDetails.email.trim())
				: false,
		};

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		} else {
			const data = {
				// employeeId,
				isDirector: false,
				...identificationDetails,
				...personalDetails,
				...employeeDetails,
				dateOfBirth: formatToDateOnly(personalDetails.dateOfBirth),
				joiningDate: formatToDateOnly(employeeDetails.joiningDate),
				skipLeaveGratuity:
					(employeeDetails.skipLeaveGratuity as any as string) === 'true' ||
					(employeeDetails.skipLeaveGratuity as any) === true,
				relievingDate: identificationDetails.relievingDate
					? formatToDateOnly(identificationDetails.relievingDate)
					: null,
				contractInDate: identificationDetails.contractInDate
					? formatToDateOnly(identificationDetails.contractInDate)
					: null,
				contractOutDate: identificationDetails.contractOutDate
					? formatToDateOnly(identificationDetails.contractOutDate)
					: null,
				passportNo: identificationDetails.passportNo
					? identificationDetails.passportNo
					: null,
				referenceNumber: personalDetails.referenceNumber
					? personalDetails.referenceNumber
					: null,
				isSSNVerified,
				isNRCVerified,
			};

			setIsLoading(true);
			if (!searchParams.get('employeeId')) {
				try {
					const createdEmployee = await employeeApi.createEmployee(data);
					setEmployeeId(createdEmployee?.data?.data?.employeeId);
					toastText('Employee on boarded successfully', 'success');
					if (permissionsContext.allowedPermissions.includes('Edit_Taxation')) {
						changeTab('taxationDetails');
					}
					setSearchParams({
						employeeId: createdEmployee?.data?.data?.id,
					});
					getEmployeeDetail(createdEmployee?.data?.data?.id);
				} catch (err: any) {
					const message =
						err?.response?.data?.message ||
						'Something went wrong in creating employee';
					toastText(message, 'error');
				}
			} else {
				try {
					const employee = await employeeApi.updateEmployee(
						searchParams.get('employeeId') as string,
						data,
					);
					let message =
						employee?.data?.message || 'Employee updated successfully';
					toastText(message, 'success');
					getEmployeeDetail(searchParams.get('employeeId'));
					if (permissionsContext.allowedPermissions.includes('Edit_Taxation')) {
						changeTab('taxationDetails');
					}
				} catch (err: any) {
					const message =
						err?.response?.data?.message ||
						'Something went wrong in updating employee';
					toastText(message, 'error');
				}
			}
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (employeeData) {
			setIsNRCVerified(employeeData.isNRCVerified);
			setIsSSNRequired(employeeData.isSSNVerified);
			const updatedPersonalDetails: any = { ...personalDetails };
			const updatedIdentificationDetails: any = {
				...identificationDetails,
			};
			const age = calculateAge(
				updatedIdentificationDetails.dateOfBirth as string,
			);

			age > napsaAgeLimit ? setIsSSNRequired(false) : setIsSSNRequired(true);
			age > nhimaAgeLimit
				? setIsNhimaRequired(false)
				: setIsNhimaRequired(true);

			const updatedEmployeeDetails: any = { ...employeeDetails };

			for (const key in employeeData) {
				if (key in updatedPersonalDetails) {
					updatedPersonalDetails[key] = employeeData[key];
				} else if (key in updatedIdentificationDetails) {
					updatedIdentificationDetails[key] = employeeData[key];
				} else if (key in updatedEmployeeDetails) {
					updatedEmployeeDetails[key] = employeeData[key];
				}
			}

			if (employeeData.isSSNVerified !== undefined) {
				setIsSSNVerified(employeeData.isSSNVerified);
			}

			if (employeeData.isNRCVerified !== undefined) {
				setIsNRCVerified(employeeData.isNRCVerified);
			}

			setPersonalDetails({
				...updatedPersonalDetails,
				dateOfBirth: dayjs(updatedPersonalDetails.dateOfBirth),
			});
			setIdentificationDetails({
				...updatedIdentificationDetails,
				relievingDate: updatedIdentificationDetails.relievingDate
					? dayjs(updatedIdentificationDetails.relievingDate)
					: null,
				contractInDate: updatedIdentificationDetails.contractInDate
					? dayjs(updatedIdentificationDetails.contractInDate)
					: null,
				contractOutDate: updatedIdentificationDetails.contractOutDate
					? dayjs(updatedIdentificationDetails.contractOutDate)
					: null,
			});
			setEmployeeDetails({
				...updatedEmployeeDetails,
				joiningDate: dayjs(updatedEmployeeDetails.joiningDate),
			});

			setStoredStatus(updatedIdentificationDetails.recordStatus);
		}
	}, [employeeData]);

	const formatInputValue = (value: string) => {
		// Remove any non-numeric characters
		const numericValue = value.replace(/\D/g, '');

		// Split the numeric value into parts based on the format 000000/00/0
		const part1 = numericValue.substring(0, 6);
		const part2 = numericValue.substring(6, 8);
		const part3 = numericValue.substring(8, 9);

		let formattedValue = part1;
		if (part2) {
			formattedValue += '/' + part2;
		}
		if (part3) {
			formattedValue += '/' + part3;
		}

		return formattedValue;
	};

	// Render verification status badge for SSN
	const renderSSNVerificationStatus = () => {
		if (isSSNVerified) {
			return (
				<span style={{ color: '#52c41a', marginLeft: '5px' }}>
					<CheckCircleFilled /> Verified
				</span>
			);
		} else if (identificationDetails.ssn) {
			return (
				<span style={{ color: '#f5222d', marginLeft: '5px' }}>
					<CloseCircleFilled /> Not Verified
				</span>
			);
		}
		return null;
	};

	// Render verification status badge for NRC
	const renderNRCVerificationStatus = () => {
		if (isNRCVerified) {
			return (
				<span style={{ color: '#52c41a', marginLeft: '5px' }}>
					<CheckCircleFilled /> Verified
				</span>
			);
		} else if (identificationDetails.nationalRegistrationNo) {
			return (
				<span style={{ color: '#f5222d', marginLeft: '5px' }}>
					<CloseCircleFilled /> Not Verified
				</span>
			);
		}
		return null;
	};

	const items: CollapseProps['items'] = [
		{
			key: '1',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Personal Details
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={6} className={styles['col']}>
						<InputField
							name="firstName"
							value={personalDetails.firstName}
							label="First Name"
							required={true}
							regex="^[a-zA-Z ]+$"
							helperText="First name must contain letters and spaces"
							onChange={(value) => {
								handleChange(value, 'firstName', 'personalDetails', true);
							}}
							isError={formError.firstName}
							disabled={isDisabled}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<InputField
							name="midName"
							value={personalDetails.midName}
							label="Middle Name"
							required={false}
							helperText="Middle name must contain letters and spaces"
							onChange={(value) => {
								handleChange(
									value,
									'midName',
									'personalDetails',
									false,
									/^[a-zA-Z ]+$/,
								);
							}}
							disabled={isDisabled}
							isError={formError.midName}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<InputField
							name="lastName"
							value={personalDetails.lastName}
							label="Last Name"
							required={true}
							regex="^[a-zA-Z ]+$"
							helperText="Last name must contain letters and spaces"
							onChange={(value) => {
								handleChange(value, 'lastName', 'personalDetails', true);
							}}
							disabled={isDisabled}
							isError={formError.lastName}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<DatePickerField
							label="DOB"
							name="dateOfBirth"
							value={personalDetails.dateOfBirth}
							required={true}
							helperText="Date of Birth is required"
							placeholder="DOB"
							onChange={(value) => {
								handleChange(value, 'dateOfBirth', 'personalDetails', true);
							}}
							disabled={isDisabled}
							isError={formError.dateOfBirth}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							placeholder="Gender"
							options={dropdownOptions?.gender}
							value={personalDetails.gender}
							onChange={(value) => {
								handleChange(value, 'gender', 'personalDetails', false);
							}}
							size="large"
							required={false}
							helperText="Gender is required"
							label="Gender"
							disabled={isDisabled}
							isError={formError.gender}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							placeholder="Country"
							options={dropdownOptions?.country}
							value={personalDetails.country}
							onChange={(value) => {
								handleChange(value, 'country', 'personalDetails', true);
							}}
							size="large"
							required={true}
							helperText="Country is required"
							label="Country"
							disabled={isDisabled}
							isError={formError.country}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							placeholder="Marital Status"
							options={dropdownOptions?.maritalStatus}
							value={personalDetails.maritalStatus}
							onChange={(value) => {
								handleChange(value, 'maritalStatus', 'personalDetails', false);
							}}
							size="large"
							required={false}
							helperText="Marital Status is required"
							label="Marital Status"
							disabled={isDisabled}
							isError={formError.maritalStatus}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<InputField
							value={personalDetails.referenceNumber}
							name="referenceNumber"
							label="Reference Number"
							required={false}
							helperText="Reference Number can only contain letters, numbers, and dashes"
							regex="^(?!-+$)[a-zA-Z0-9-]+$"
							onChange={(value) => {
								handleChange(
									value,
									'referenceNumber',
									'personalDetails',
									false,
									/^(?!-+$)[a-zA-Z0-9-]+$/,
								);
							}}
							isError={formError.referenceNumber}
							disabled={isDisabled}
							extraLabel={
								<Tooltip
									title={'External System Unique Identifier'}
									placement="top"
								>
									<InfoCircleOutlined
										style={{
											paddingInline: '0.5rem',
											color: '#584495',
										}}
									/>
								</Tooltip>
							}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<InputField
							name="email"
							value={personalDetails.email ?? ''}
							label="Email"
							required={false}
							helperText="Invalid email format"
							onChange={(value) => {
								handleChange(value, 'email', 'personalDetails', false);
							}}
							isError={formError.email}
							disabled={isDisabled}
						/>
					</Col>
				</Row>
			),
		},
		{
			key: '2',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Employee Details
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={6} className={styles['col']}>
						<DatePickerField
							label="Joining Date"
							name="joiningDate"
							value={employeeDetails.joiningDate}
							required={true}
							helperText="Joining Date is required"
							disabledBeforeDates={personalDetails.dateOfBirth}
							placeholder="Joining Date"
							onChange={(value) => {
								handleChange(value, 'joiningDate', 'employeeDetails', true);
							}}
							disabled={isDisabled || invalidText(personalDetails.dateOfBirth)}
							isError={formError.joiningDate}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Branch"
							placeholder="Select Branch"
							options={accessibleBranches}
							value={employeeDetails.branchId}
							onChange={(value) => {
								handleChange(value, 'branchId', 'employeeDetails', true);
							}}
							size="large"
							required={true}
							helperText="Branch is required"
							label="Branch"
							disabled={isDisabled || isPayrollRun}
							isError={formError.branchId}
							loading={isBranchesLoading}
							extraLabel={
								isPayrollRun && (
									<Tooltip
										title={
											'You can change the branch after a current payroll is approved/finalized'
										}
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												paddingInline: '0.5rem',
												color: '#584495',
											}}
										/>
									</Tooltip>
								)
							}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Department"
							placeholder="Select Department"
							options={
								employeeDetails.branchId
									? dropdownOptions?.department?.filter(
										(item: any) => item.branchId === employeeDetails.branchId,
									)
									: []
							}
							value={employeeDetails.departmentId}
							onChange={(value) => {
								handleChange(value, 'departmentId', 'employeeDetails', false);
							}}
							size="large"
							required={false}
							helperText="Department is required"
							label="Department"
							disabled={isDisabled || isPayrollRun}
							isError={formError.departmentId}
							extraLabel={
								isPayrollRun && (
									<Tooltip
										title={
											'You can change the department after a current payroll is approved/finalized'
										}
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												paddingInline: '0.5rem',
												color: '#584495',
											}}
										/>
									</Tooltip>
								)
							}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Cost Center"
							placeholder="Select Cost Center"
							options={
								employeeDetails?.departmentId
									? dropdownOptions?.costCenter?.filter(
										(item: any) =>
											item.departmentId === employeeDetails.departmentId,
									)
									: []
							}
							value={employeeDetails.costCenterId}
							onChange={(value) => {
								handleChange(value, 'costCenterId', 'employeeDetails', false);
							}}
							size="large"
							required={false}
							helperText="Cost center is required"
							label="Cost Center"
							disabled={isDisabled}
							isError={formError.costCenterId}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Category"
							placeholder="Category"
							options={dropdownOptions?.category}
							value={employeeDetails.categoryId}
							onChange={(value) => {
								handleChange(value, 'categoryId', 'employeeDetails', true);
							}}
							size="large"
							required={true}
							helperText="Category is required"
							label="Category"
							disabled={isDisabled}
							isError={formError.categoryId}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Job Title"
							placeholder="Job Title"
							options={dropdownOptions?.jobGrade}
							value={employeeDetails.jobGradeId}
							onChange={(value) => {
								handleChange(value, 'jobGradeId', 'employeeDetails', false);
							}}
							size="large"
							required={false}
							helperText="Job Title is required"
							label="Job Title"
							disabled={isDisabled || isPayrollRun}
							extraLabel={
								isPayrollRun && (
									<Tooltip
										title={
											'You can change the job title after a current payroll is approved/finalized'
										}
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												paddingInline: '0.5rem',
												color: '#584495',
											}}
										/>
									</Tooltip>
								)
							}
							isError={formError.jobGradeId}
						/>
					</Col>
					<Col span={6}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Staff Code"
							placeholder="Staff Code"
							options={dropdownOptions?.staffCode}
							value={employeeDetails.staffCode}
							onChange={(value) => {
								handleChange(value, 'staffCode', 'employeeDetails', true);
							}}
							size="large"
							required={true}
							helperText="Staff Code is required"
							label="Staff Code"
							disabled={isDisabled || isPayrollRun}
							isError={formError.staffCode}
							extraLabel={
								isPayrollRun && (
									<Tooltip
										title={
											'You can change the staff Code after a current payroll is approved/finalized'
										}
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												paddingInline: '0.5rem',
												color: '#584495',
											}}
										/>
									</Tooltip>
								)
							}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="No"
							placeholder="Skip Leave & Gratuity"
							isError={false}
							options={booleanOptions}
							value={String(employeeDetails.skipLeaveGratuity ?? false)}
							onChange={(value) => {
								handleChange(
									value,
									'skipLeaveGratuity',
									'employeeDetails',
									false,
								);
							}}
							size="large"
							required={false}
							helperText="When enabled, Leave Pay and Gratuity Pay will not accrue for this employee."
							label="Skip Leave & Gratuity?"
							disabled={isDisabled || skipLeaveGratuityLocked}
							extraLabel={
								<Tooltip
									title="When enabled, Leave Pay and Gratuity Pay will not accrue for this employee. This setting can only be modified before the employee's first payroll run."
									placement="top"
								>
									<InfoCircleOutlined
										style={{
											paddingInline: '0.5rem',
											color: '#584495',
										}}
									/>
								</Tooltip>
							}
						/>
					</Col>
				</Row>
			),
		},

		{
			key: '3',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Identification Information
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					{' '}
					<Col span={6} className={styles['col']}>
						<div className="input-field">
							<p
								className="label"
								style={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								SSN {isSSNRequired && <span className="red">&nbsp;*</span>}
								{stage === 'live' && renderSSNVerificationStatus()}
							</p>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
								}}
							>
								<div style={{ flex: 1 }}>
									<Input
										name="ssn"
										status={formError.ssn ? 'error' : ''}
										placeholder="Enter SSN"
										value={identificationDetails?.ssn as string}
										required={isSSNRequired}
										onChange={(e) => {
											handleChange(
												e.target.value,
												'ssn',
												'identificationDetails',
												isSSNRequired,
												/^(\d{9})?$/,
											);
											if (e.target.value !== identificationDetails.ssn) {
												setIsSSNVerified(false);
											}
										}}
										type={isSSNVisible ? 'text' : 'password'}
										size="large"
										disabled={isDisabled}
										suffix={
											isSSNVisible ? (
												<EyeOutlined
													onClick={() => {
														setIsSSNVisible((prev) => !prev);
													}}
												/>
											) : (
												<EyeInvisibleOutlined
													onClick={() => {
														setIsSSNVisible((prev) => !prev);
													}}
												/>
											)
										}
									/>
									{formError.ssn && (
										<p
											className="red"
											style={{
												fontSize: '12px',
												marginLeft: '2px',
											}}
										>
											SSN must be a 9-digit number
										</p>
									)}
								</div>
								{identificationDetails.ssn &&
									isSSNRequired &&
									!isSSNVerified &&
									stage === 'live' &&
									!formError.ssn && (
										<Button
											type="primary"
											loading={isVerifyingSSN}
											onClick={handleVerifySSN}
											disabled={isDisabled || isVerifyingSSN}
											style={{ minWidth: '100px' }}
										>
											Verify
										</Button>
									)}
							</div>
						</div>
					</Col>
					<Col span={5}>
						<InputField
							name="passportNo"
							value={identificationDetails?.passportNo}
							label="Passport No."
							required={false}
							onChange={(value) => {
								handleChange(
									value,
									'passportNo',
									'identificationDetails',
									false,
								);
							}}
							disabled={isDisabled}
							isError={false}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<InputField
							name="nhimaNo"
							value={employeeDetails?.nhimaNo as string}
							helperText="NHIMA Number is required"
							label="NHIMA Number"
							onChange={(value) => {
								handleChange(
									value,
									'nhimaNo',
									'employeeDetails',
									isNhimaRequired,
								);
							}}
							type={'text'}
							required={isNhimaRequired}
							disabled={isDisabled}
							isError={formError.nhimaNo}
						/>
					</Col>
					<Col span={7} className={styles['col']}>
						<div className="input-field">
							<p
								className="label"
								style={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								National Registration No. <span className="red">&nbsp;*</span>
								{stage === 'live' && renderNRCVerificationStatus()}
							</p>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
								}}
							>
								<div style={{ flex: 1 }}>
									<Input
										name="nationalRegistrationNo"
										status={formError.nationalRegistrationNo ? 'error' : ''}
										placeholder="000000/00/0"
										value={identificationDetails.nationalRegistrationNo}
										required={true}
										onChange={(e) => {
											const formattedValue = formatInputValue(e.target.value);
											handleChange(
												formattedValue,
												'nationalRegistrationNo',
												'identificationDetails',
												true,
												null,
											);
											if (
												formattedValue !==
												identificationDetails.nationalRegistrationNo
											) {
												setIsNRCVerified(false);
											}
										}}
										size="large"
										disabled={isDisabled}
									/>
									{formError.nationalRegistrationNo && (
										<p
											className="red"
											style={{
												fontSize: '12px',
												marginLeft: '2px',
											}}
										>
											Invalid national registration no.
										</p>
									)}
								</div>
								{identificationDetails.nationalRegistrationNo &&
									!isNRCVerified &&
									stage === 'live' &&
									!formError.nationalRegistrationNo && (
										<Button
											type="primary"
											loading={isVerifyingNRC}
											onClick={handleVerifyNRC}
											disabled={isDisabled || isVerifyingNRC}
											style={{ minWidth: '100px' }}
										>
											Verify
										</Button>
									)}
							</div>
						</div>
					</Col>
				</Row>
			),
		},

		{
			key: '4',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Status
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					{' '}
					<Col span={6} className={styles['col']}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Status"
							placeholder="Select Status"
							options={dropdownOptions?.recordStatus}
							value={identificationDetails.recordStatus}
							onChange={(value) => {
								handleChange(
									value,
									'recordStatus',
									'identificationDetails',
									true,
								);
							}}
							size="large"
							required={true}
							helperText="Record status is required"
							label="Record status"
							disabled={isDisabled || isPayrollRun}
							isError={formError.recordStatus}
							extraLabel={(() => {
								const showPayroll = isPayrollRun;
								const showRejoin =
									employeeData?.recordStatus &&
									employeeData.recordStatus !== 'ACTIVE';
								if (!showPayroll && !showRejoin) return null;

								const parts = [
									showPayroll &&
										'You can change the status after a current payroll is approved or finalized',
									showRejoin && getRejoinTooltip(employeeData),
								].filter(Boolean) as string[];

								return (
									<Tooltip
										title={
											parts.length > 1 ? (
												<>
													{parts[0]}
													<br />
													{parts[1]}
												</>
											) : (
												parts[0]
											)
										}
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												paddingInline: '0.5rem',
												color:
													showRejoin && !employeeData.isEligibleToRejoin
														? '#faad14'
														: '#584495',
											}}
										/>
									</Tooltip>
								);
							})()}
						/>
						{identificationDetails.statusUpdatedAt && (
							<div className={styles['helper-text']}>
								Updated At :{' '}
								{dayjs(identificationDetails?.statusUpdatedAt).format(
									'DD/MM/YYYY hh:mm A',
								)}
							</div>
						)}
					</Col>
					<Col span={6} className={styles['col']}>
						{/* Custom label + tooltip row */}
						<div className={styles['field-label']}>
							<span className={styles['label-text']}>Last Working Day</span>
							{showRelievingInfo && (
								<Tooltip
									title="Relieving date is not required for active employees and has been reset"
									placement="top"
								>
									<ExclamationCircleOutlined
										style={{
											color: '#584495',
											marginLeft: 8,
											fontSize: '14px',
										}}
									/>
								</Tooltip>
							)}
							{isPayrollRun && identificationDetails.relievingDate && (
								<Tooltip
									title={
										'You can change the relieving date after a current payroll is approved/finalized'
									}
									placement="top"
								>
									<InfoCircleOutlined
										style={{
											paddingInline: '0.5rem',
											color: '#584495',
										}}
									/>
								</Tooltip>
							)}
						</div>

						<DatePickerField
							name="relievingDate"
							value={identificationDetails.relievingDate as string}
							required={false}
							placeholder="Last Working Day"
							onChange={(value) => {
								handleChange(
									value,
									'relievingDate',
									'identificationDetails',
									true,
								);
							}}
							disabledBeforeDates={
								lastDateOfPayPeriod
									? new Date(
										new Date(lastDateOfPayPeriod).setHours(23, 59, 59, 999),
									)
									: dayjs(employeeDetails.joiningDate)
							}
							disabled={
								isDisabled ||
								invalidText(employeeDetails.joiningDate) ||
								identificationDetails.recordStatus === 'ACTIVE'
							}
							isError={formError.relievingDate}
							allowClear={true}
						/>
					</Col>
					<Col span={6} className={styles['col']}>
						<div className={styles['field-label']}>
							<span className={styles['label-text']}>Contract period</span>
						</div>
						<DatePicker.RangePicker
							size="large"
							style={{ width: '100%' }}
							format="DD/MM/YYYY"
							placeholder={['Contract start', 'Contract end']}
							value={
								identificationDetails.contractInDate &&
									identificationDetails.contractOutDate
									? [
										dayjs(identificationDetails.contractInDate),
										dayjs(identificationDetails.contractOutDate),
									]
									: null
							}
							onChange={(dates) => {
								if (!dates || !dates[0] || !dates[1]) {
									setIdentificationDetails((prev) => ({
										...prev,
										contractInDate: null,
										contractOutDate: null,
									}));
									return;
								}
								if (!dates[1].isAfter(dates[0], 'day')) {
									toastText(
										'Contract end date must be after the contract start date. Same-day contracts are not allowed.',
										'error',
									);
									return;
								}
								setIdentificationDetails((prev) => ({
									...prev,
									contractInDate: dates[0],
									contractOutDate: dates[1],
								}));
							}}
							disabled={isDisabled}
							allowClear
							disabledDate={(current, info) => {
								if (!current) {
									return false;
								}
								if (
									employeeDetails.joiningDate &&
									current.isBefore(
										dayjs(employeeDetails.joiningDate).startOf('day'),
										'day',
									)
								) {
									return true;
								}
								if (
									identificationDetails.relievingDate &&
									current.isAfter(
										dayjs(identificationDetails.relievingDate).startOf('day'),
										'day',
									)
								) {
									return true;
								}
								if (
									info.from &&
									!current.isAfter(dayjs(info.from).startOf('day'), 'day')
								) {
									return true;
								}
								return false;
							}}
						/>
					</Col>
				</Row>
			),
		},
	];
	return (
		<>
			<div className={styles['employment']}>
				<Collapse
					items={items}
					defaultActiveKey={['1', '2', '3', '4']}
					expandIconPosition="end"
					className={styles['employment--employeeDetails']}
					expandIcon={({ isActive }) => (
						<div
							style={{
								transform: `rotate(${isActive ? -90 : 0}deg)`,
								transition: '0.3s',
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								fill="currentColor"
								className="bi bi-chevron-right"
								viewBox="0 0 16 16"
							>
								<path
									fillRule="evenodd"
									d="M1.646 4.646a.5.5 0 0 1 0 .708l6 6a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708.708L2.707 4.5 1.646 5.561a.5.5 0 0 1-.708-.708z"
								/>
							</svg>
						</div>
					)}
				/>
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'start',
					margin: '1rem 0rem ',
				}}
			>
				<Buttons buttons={buttons} />
			</div>
		</>
	);
};

export default EmploymentDetails;
